import { AppAPI, Entity, EntityMetricsDefinition, isNumericMetricInfo, isTagMetricInfo, MetricInfo, SmartModuleType, MetricValueType }
    from '@moderepo/mode-apis';
import { TFunction } from 'react-i18next';
import { EntityLatestSingleMetricValueUIComponentCustomSettings } from '..';
import { getMetricsDefinitionMetricsId } from '../..';
import { getDateTimeDisplay } from '../../../containerUtils';


/**
 * This is the interface that is used for storing all the info we needed to display the value for 1 metric
 */
export interface SingleMetricData {
    readonly name: string;
    readonly value: string;
    readonly unit?: string | undefined;
    readonly date?: string | undefined;
}



/**
 * Fetch data from collection
 */
const fetchDataFromCollection = async (
    metricValueType: MetricValueType,
    entity: Entity,
    metricsDef: EntityMetricsDefinition,
    metricInfo: MetricInfo,
    trans: TFunction,
): Promise<SingleMetricData | undefined> => {
    const collectionId = getMetricsDefinitionMetricsId(entity, metricsDef.metricsDefinitionId);

    // "metricValueType" which can be 'number' for numeric metric or 'string' for tagMetric. If it is 'number', we will need to call the
    // GET collection data API and pass an array of valueNames. If it is 'string', we will need to pass an array of tagNames.
    const valueNames = metricValueType === 'number' ? [metricInfo.name] : [];
    const tagNames = metricValueType === 'string' ? [metricInfo.name] : [];

    try {
        // Make API call to get 1 RAW data point
        const response = await AppAPI.getInstance().getTimeSeriesCollectionRawData(
            entity.homeId, metricsDef.dataStoreConfiguration.moduleInstanceId, collectionId, (new Date()).toISOString(), -1, valueNames, tagNames,
        );

        // Make sure there is at least 1 row of data returned.
        if (response && response.data && response.data.length > 0) {
            const dataRow = response.data[0];

            // Check if the row has some value. The first element in the data row is the date and the second element should be the value for
            // the metric we wanted to fetch
            if (dataRow.length > 1) {

                // Get the value from the data row, NOTE: data can be null. If data is null, we will show "-"
                const value = dataRow[1];

                // If metricInfo is a tagMetricInfo, there might be an ENUM mapping of the value to some user-friendly display value
                // Therefore, we will see if there is an enum definition that matches the "value"
                // If we found an enum def for the "value" then use the enumDef.name, else use the value as is as the value to be displayed
                const enumDef = isTagMetricInfo(metricInfo) ? metricInfo.enumDefinitions?.find((enumDef) => {
                    return enumDef.value === value;
                }) : undefined;

                const displayValue = enumDef ? enumDef.name : value?.toString() ?? '-';

                return {
                    name : metricInfo.displayName ?? metricInfo.name,
                    value: displayValue,
                    unit : isNumericMetricInfo(metricInfo) ? metricInfo.unit : undefined,
                    date : getDateTimeDisplay(dataRow[0], trans),
                };
            }
        }
    } catch (error) {
        // Do nothing
    }

    return {
        name : metricInfo.displayName ?? metricInfo.name,
        value: '-',
        unit : isNumericMetricInfo(metricInfo) ? metricInfo.unit : undefined,
    };
};



/**
 * Fetch data for the specified metricsDefinition and metricInfo. This function will check the metricsDefinition's data source type and call
 * the function to fetch data accordingly.
 * For now, we will only support TSDB data source. We can add support for other data sources later.
 */
export const fetchSingleMetricData = async (
    customSettings: EntityLatestSingleMetricValueUIComponentCustomSettings,
    metricValueType: MetricValueType | undefined,
    entity: Entity,
    metricsDef: EntityMetricsDefinition,
    metricInfo: MetricInfo | undefined,
    trans: TFunction,
): Promise<SingleMetricData | undefined> => {
    if (metricsDef.dataStoreConfiguration.moduleType === SmartModuleType.TSDB && metricInfo && metricValueType) {
        return fetchDataFromCollection(metricValueType, entity, metricsDef, metricInfo, trans);
    }
    return undefined;
};
